import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    ICreateGroupModel,
    IGroupCategoryModel,
    IGroupInviteModel,
    IGroupInvitesListingFilterModel,
    IGroupModel,
    IGroupReportModel,
    IGroupsListingFilterModel,
    IUpdateGroupDetailModel,
} from '../../../models/groups.model';
import { IPostListingFilterModel, IPostModel } from '../../../models/post.model';
import { IShareUserAPIResponse, IUserModel } from '../../../models/user.model';
import { BaseApiService } from '../../base-api/base-api.service';

@Injectable({
    providedIn: 'root',
})
export class GroupsService extends BaseApiService {
    protected override endPoint = '/groups';

    createGroup(data: ICreateGroupModel): Observable<IGroupModel> {
        const payload = {
            name: data.name,
            description: data.description,
            ...(data.privacy ? { privacy: data.privacy } : {}),
            ...(data.photo ? { photo: data.photo } : {}),
            ...(data.cats && data.cats.length ? { cats: data.cats } : {}),
        };

        return this.http.post<IGroupModel>(`${this.fullPath}`, payload);
    }

    getGroupsListing(filter: IGroupsListingFilterModel = {}): Observable<{ total: number; result: IGroupModel[] }> {
        const params = {
            filter: filter.filter ?? 'joined',
            sort: filter.sort ?? 'name_desc',
            page: filter.page ?? 1,
            size: filter.size ?? 999,
            ...(filter.cats ? { cats: filter.cats } : {}),
        };

        return this.http.get<{ total: number; result: IGroupModel[] }>(`${this.fullPath}`, { params });
    }

    getGroupDetail(groupId: number): Observable<IGroupModel> {
        return this.http.get<IGroupModel>(`${this.fullPath}/${groupId}`);
    }

    updateGroupDetail(groupId: number, data: IUpdateGroupDetailModel): Observable<IGroupModel> {
        return this.http.put<IGroupModel>(`${this.fullPath}/${groupId}`, data);
    }

    pinGroup(groupId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/pin`, {});
    }

    unpinGroup(groupId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/pin`);
    }

    muteGroup(groupId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/notifications/mute`, {});
    }

    unmuteGroup(groupId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/notifications/mute`);
    }

    generateShareGroupLink(groupId: number): Observable<IShareUserAPIResponse> {
        return this.http.post<IShareUserAPIResponse>(`${this.fullPath}/${groupId}/share`, {});
    }

    getGroupPostsListing(groupId: number, filter: IPostListingFilterModel = {}): Observable<IPostModel[]> {
        const params = {
            sort: filter.sort ?? 'latest',
            page: filter.page ?? 1,
            size: filter.size ?? 20,
            ...(filter.cats?.length ? { cats: filter.cats.join(',') } : {}),
        };

        return this.http.get<IPostModel[]>(`${this.fullPath}/${groupId}/posts`, { params });
    }

    getPinnedGroupPostsListing(
        groupId: number,
        filter: IPostListingFilterModel = {},
    ): Observable<{ total: number; result: IPostModel[] }> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 999,
        };

        return this.http.get<{ total: number; result: IPostModel[] }>(`${this.fullPath}/${groupId}/posts/pinned`, {
            params,
        });
    }

    pinGroupPost(groupId: number, postId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/posts/${postId}/pin`, {});
    }

    unpinGroupPost(groupId: number, postId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/posts/${postId}/pin`, {});
    }

    getGroupMembersListing(
        groupId: number,
        filter: IPostListingFilterModel = {},
    ): Observable<{ total: number; result: IUserModel[] }> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 20,
            ...(filter.keyword ? { keyword: filter.keyword } : {}),
        };

        return this.http.get<{ total: number; result: IUserModel[] }>(
            `${this.apiServer}/v2/groups/${groupId}/members`,
            { params },
        );
    }

    getGroupMemberJoinRequestsListing(
        groupId: number,
        filter: IGroupsListingFilterModel = {},
    ): Observable<{ total: number; result: IUserModel[] }> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 999,
        };

        return this.http.get<{ total: number; result: IUserModel[] }>(`${this.fullPath}/${groupId}/members/request`, {
            params,
        });
    }

    getGroupCategoryListing(pageSize: number, keyword?: string): Observable<IGroupCategoryModel[]> {
        const params = {
            page: 1,
            size: pageSize,
            ...(keyword ? { keyword } : {}),
        };

        return this.http.get<IGroupCategoryModel[]>(`${this.fullPath}/cats`, { params });
    }

    getGroupReportsListing(): Observable<IGroupReportModel[]> {
        return this.http.get<IGroupReportModel[]>(`${this.fullPath}/posts/report`);
    }

    joinGroup(groupId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/join`, {});
    }

    leaveGroup(groupId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/leave`, {});
    }

    inviteMember(groupId: number, users: number[]): Observable<void> {
        const payload = { users };
        return this.http.post<void>(`${this.fullPath}/${groupId}/invite/users`, payload);
    }

    removeMember(groupId: number, userId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/members/${userId}`);
    }

    assignModerator(groupId: number, userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/members/${userId}/moderator`, {});
    }

    demoteModerator(groupId: number, userId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/members/${userId}/moderator`);
    }

    assignAdmin(groupId: number, userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/members/${userId}/admin`, {});
    }

    demoteAdmin(groupId: number, userId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/members/${userId}/admin`);
    }

    approveMemberJoinGroup(groupId: number, userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/members/${userId}/approve`, {});
    }

    rejectMemberJoinGroup(groupId: number, userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/members/${userId}/reject`, {});
    }

    getGroupInvitesListing(
        filter: IGroupInvitesListingFilterModel = {},
    ): Observable<{ total: number; result: IGroupInviteModel[] }> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 20,
        };

        return this.http.get<{ total: number; result: IGroupInviteModel[] }>(`${this.fullPath}/invite/request`, {
            params,
        });
    }

    acceptGroupInvite(groupId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${groupId}/invite/request`, {});
    }

    rejectGroupInvite(groupId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${groupId}/invite/request`, {});
    }
}
